<template>
    <SignaloDialog
        :icon="'mdi-cog-outline'"
        :title="
            currentlyEditedUser?.id ? $t('staff.editUser') : $t('staff.addUser')
        "
        :open="open"
        @close="() => closeDialog()"
    >
        <template #body>
            <SignaloPageLoading v-if="loading" />
            <v-card-text v-else>
                <v-form ref="userDataForm" v-model="formValid">
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <div
                                class="d-flex flex-wrap"
                                v-if="currentlyEditedUser.id !== undefined"
                            >
                                <SignaloImageUpload
                                    v-if="userPhoto"
                                    v-model="userPhoto"
                                    :uploadEndpoint="`/users/${currentlyEditedUser.id}/photos`"
                                    :removeEndpoint="`/users/${currentlyEditedUser.id}/photos/${userPhoto.id}`"
                                    @image-removed="onRemoveImageHandler"
                                    @update:modelValue="onImageUploadedHandler"
                                    :allowDraw="false"
                                    :storageUrl="this.getFileStorageUrl() + '/'"
                                />
                                <div
                                    v-else
                                    class="eq-photo d-flex justify-center align-center"
                                    @click="addPhotoClicked"
                                >
                                    <v-icon size="x-large">
                                        mdi-plus-circle-outline
                                    </v-icon>
                                    <div class="cover"></div>
                                    <input
                                        type="file"
                                        ref="fileInput"
                                        @change="filesSelected"
                                        style="display: none"
                                        accept="image/png, image/jpeg, image/bmp, image/webp"
                                    />
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                            <v-text-field
                                :label="$t('staff.nameAndLastname')"
                                required
                                :rules="nameRules"
                                v-model="currentlyEditedUser.name"
                                variant="outlined"
                                color="var(--greyscale-100)"
                                class="required-field"
                            >
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                            <v-select
                                :label="$t('common.timezone')"
                                v-model="currentlyEditedUser.timezone"
                                :items="timezones"
                                variant="outlined"
                                color="var(--greyscale-100)"
                                class="required-field"
                            >
                            </v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="6" xl="6">
                            <v-select
                                :label="$t('staff.accountStatus')"
                                :items="accountStatusOptions"
                                variant="outlined"
                                color="var(--greyscale-100)"
                                v-model="currentlyEditedUser.account_status"
                                item-title="label"
                                item-value="value"
                                class="required-field"
                            ></v-select
                        ></v-col>
                        <v-col cols="12" md="6" xl="6">
                            <SignaloNewValueSelect
                                :selectableType="SELECTABLE_TYPES.DICTIONARY"
                                dictionary="user_criticality"
                                v-model="currentlyEditedUser.user_criticality"
                                clearable
                                :label="$t('searchConfig.criticality')"
                        /></v-col>
                    </v-row>
                    <v-row
                        v-if="
                            !this.currentlyEditedUser?.id &&
                            dataStore?.userTemplates?.items?.length
                        "
                    >
                        <v-col cols="12" md="12" xl="12">
                            <v-select
                                variant="outlined"
                                color="var(--greyscale-100)"
                                :label="$t('common.profile')"
                                v-model="userTemplate"
                                :items="dataStore.userTemplates.items"
                                :item-title="
                                    (item) =>
                                        $getPropertyTranslation(item?.name)
                                "
                                return-object
                                clearable
                                @click:clear="userTemplate = null"
                                clear-icon="mdi-close-circle-outline"
                            >
                            </v-select
                        ></v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6" md="6">
                            <v-text-field
                                :label="$t('staff.phoneNumber')"
                                maxlength="13"
                                v-model="currentlyEditedUser.phone"
                                variant="outlined"
                                color="var(--greyscale-100)"
                                :rules="[$PHONE_NUMBER_RULE]"
                                :disabled="
                                    !authStore.isAllowed(
                                        'staff.contact',
                                        'update'
                                    )
                                "
                            >
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                            <v-text-field
                                :label="$t('common.email')"
                                maxlength="255"
                                :rules="_emailRules"
                                v-model="currentlyEditedUser.email"
                                variant="outlined"
                                color="var(--greyscale-100)"
                                :disabled="
                                    !authStore.isAllowed(
                                        'staff.contact',
                                        'update'
                                    )
                                "
                            >
                            </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                            <v-text-field
                                :label="$t('common.employeeNumber')"
                                maxlength="255"
                                v-model="currentlyEditedUser.no"
                                variant="outlined"
                                color="var(--greyscale-100)"
                            >
                            </v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6" md="6">
                            <SignaloNewValueSelect
                                :selectableType="SELECTABLE_TYPES.DICTIONARY"
                                dictionary="employee_status"
                                v-model="currentlyEditedUser.status"
                                clearable
                                :label="$t('common.status')"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="6">
                            <SignaloNewValueSelect
                                :selectableType="SELECTABLE_TYPES.DICTIONARY"
                                dictionary="user_functions"
                                v-model="currentlyEditedUser.function"
                                clearable
                                :label="$t('common.function')"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6" md="6">
                            <SignaloNewValueSelect
                                :selectableType="SELECTABLE_TYPES.DICTIONARY"
                                :dictionary="'departments'"
                                :label="$t('staff.department')"
                                v-model="currentlyEditedUser.department"
                                clearable
                            />
                        </v-col>

                        <v-col cols="12" sm="6" md="6">
                            <SignaloNewValueSelect
                                :selectableType="SELECTABLE_TYPES.CREW"
                                v-model="currentlyEditedUser.crew_id"
                                clearable
                            />
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                            md="6"
                            v-if="
                                authStore.isAllowed('financial_data', 'create')
                            "
                        >
                            <v-text-field
                                :label="$t('common.hourlyCost')"
                                v-model="currentlyEditedUser.work_hour_cost"
                                clearable
                                @click:clear="
                                    currentlyEditedUser.work_hour_cost = null
                                "
                                variant="outlined"
                                color="var(--greyscale-100)"
                                clear-icon="mdi-close-circle-outline"
                                type="number"
                                step="0.01"
                                :rules="minHourlyCostValue"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" xl="6">
                            <v-select
                                :label="$t('common.currency')"
                                v-model="currentlyEditedUser.currency_id"
                                :items="currencies"
                                :item-title="`name[${$i18n.locale}]`"
                                item-value="id"
                                variant="outlined"
                                color="var(--greyscale-100)"
                                clear-icon="mdi-close-circle-outline"
                                class="required-field"
                            >
                            </v-select>
                        </v-col>
                        <v-col cols="12" md="12" xl="12">
                            <SignaloBarCode
                                ref="barcodeComponent"
                                :barcode="currentlyEditedUser?.barcode"
                                :editable="true"
                                @onChange="onBarcodeChangeHandler"
                                :barcodeType="'CODE128'"
                                :label="$t('common.idNumber')"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </template>
        <template #buttons>
            <v-btn text @click="() => closeDialog()">
                {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
                @click="save"
                color="primary"
                variant="elevated"
                :loading="dataSaving"
            >
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </SignaloDialog>
</template>

<script>
import * as DATA_STORE from 'signalo-vue-data-store';
import * as SIGNALO_VUE_HELPERS_STORE from 'signalo-vue-helpers-store';
import * as AUTH_STORE from 'signalo-vue-auth-store';
export default {
    props: ['user', 'open'],
    emits: ['close'],
    setup() {
        const dataStore = DATA_STORE.default();
        const helpersStore = SIGNALO_VUE_HELPERS_STORE.default();
        const authStore = AUTH_STORE.default();
        return { dataStore, helpersStore, authStore };
    },
    data() {
        return {
            dataSaving: false,
            formValid: false,
            userPhoto: null,
            timezones: [],
            crews: [],
            currentlyEditedUser: this.user ? { ...this.user } : {},
            loading: false,
            defaultShouldRefetch: false,
            minHourlyCostValue: [
                (v) => !v || v >= 0 || this.$t('errors.valueTooLow'),
            ],
            currencies: [],
            userTemplate: null,
        };
    },
    mounted() {
        if (this.user?.photo) {
            this.userPhoto = this.user.photo;
        }
        if (!this.currentlyEditedUser?.timezone) {
            this.currentlyEditedUser.timezone =
                this.authStore.tenant.default_timezone;
        }
        this.axios.get('global/currencies').then((res) => {
            this.currencies = res?.data?.items;
            if (!this.currentlyEditedUser?.currency_id) {
                this.currentlyEditedUser.currency_id =
                    this.authStore.tenant.default_currency_id;
            }
        });
        this.loadData();
    },
    methods: {
        loadData() {
            this.loading = true;
            Promise.allSettled([
                this.dataStore.dictionary.promise(),
                this.axios.get('global/timezones'),
                this.axios.get('crews'),
                this.dataStore.userTemplates.promise(),
            ]).then((res) => {
                this.timezones = res[1]?.value?.data?.items;
                this.crews = res[2]?.value?.data?.items;
                const defaultProfile = this.dataStore.userTemplates.items.find(
                    (ut) => ut.is_default
                );
                if (!this.currentlyEditedUser?.id && defaultProfile) {
                    this.userTemplate = defaultProfile;
                }
                this.loading = false;
            });
        },
        closeDialog(shouldRefetch = this.defaultShouldRefetch, newUser = null) {
            this.$emit('close', shouldRefetch, newUser);
        },
        onRemoveImageHandler() {
            this.userPhoto = null;
            this.defaultShouldRefetch = true;
        },
        onImageUploadedHandler(image) {
            this.userPhoto = image;
            this.defaultShouldRefetch = true;
        },
        async save() {
            await this.$refs.userDataForm.validate();
            if (!this.formValid) return;
            this.dataSaving = true;
            if (this.userTemplate) {
                this.currentlyEditedUser.user_template_id =
                    this.userTemplate.id;
            }
            if (!this.authStore.isAllowed('staff.contact', 'update')) {
                delete this.currentlyEditedUser.email;
                delete this.currentlyEditedUser.phone;
            }
            if (
                this.currentlyEditedUser?.barcode?.valid !== undefined &&
                this.currentlyEditedUser?.barcode?.valid !== null
            ) {
                delete this.currentlyEditedUser?.barcode.valid;
            }
            if (!this.currentlyEditedUser?.id) {
                this.axios
                    .post('/users', this.currentlyEditedUser)
                    .then((res) => {
                        this.helpersStore.snackbarSaved();
                        this.closeDialog(true, res?.data?.item);
                    })
                    .catch((e) => {
                        if (e?.response?.status == 401) return;
                        this.helpersStore.snackbarError(e);
                    })
                    .finally(() => {
                        this.dataSaving = false;
                    });
            } else {
                this.axios
                    .put(
                        `/users/${this.currentlyEditedUser.id}`,
                        this.currentlyEditedUser
                    )
                    .then((res) => {
                        this.helpersStore.snackbarSaved();
                        this.closeDialog(true, res?.data?.item);
                    })
                    .catch((e) => {
                        if (e?.response?.status == 401) return;
                        this.helpersStore.snackbarError(e);
                    })
                    .finally(() => {
                        this.dataSaving = false;
                    });
            }
        },
        addPhotoClicked() {
            this.$refs.fileInput.click();
        },
        filesSelected(e) {
            const file = e.target.files[0];
            if (file.size > this.helpersStore.MAX_FILE_SIZE) {
                this.helpersStore.snackbarWarning(
                    this.$t('errors.someFilesRejectedDueToSize') +
                        ` ${this.helpersStore.getMaxFileSizeInMB()}MB`
                );
                return;
            }
            this.userPhoto = {
                uploadFile: file,
            };
            this.defaultShouldRefetch = true;
        },
        displayImagePreview(item) {
            this.selectedImagePath = item.photo_path;
            this.imagePreview = true;
        },
        onBarcodeChangeHandler(barcode) {
            this.currentlyEditedUser.barcode = barcode;
        },
    },
    computed: {
        nameRules() {
            return [(v) => !!v || this.$t('common.nameRequired')];
        },
        accountStatusOptions() {
            return [
                { value: 'active', label: this.$t('common.active') },
                { value: 'not_activated', label: this.$t('common.notActive') },
                { value: 'disabled', label: this.$t('common.disabled') },
            ];
        },
    },
};
</script>
